import React, { useEffect, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import listPlugin from '@fullcalendar/list'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import allLocales from '@fullcalendar/core/locales-all'
import { Notify } from 'notiflix/build/notiflix-notify-aio'

import EventTab from '../components/Calendar/EventTab'
import HeaderComponent from '../components/General/HeaderComponent'
import calendarService from '../services/calendar'

export default function Calendar() {
  const calendarRef = useRef(null)
  const [caldata, setCalData] = useState([])
  const [curId, setCurId] = useState('')
  const [isActive, setIsActive] = useState(false)
  const [curDate, setCurDate] = useState(new Date())
  const [penaToken, setPenaToken] = useState()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [useComprehensiveAPI, setUseComprehensiveAPI] = useState(true) // Yeni API kullanımı

  useEffect(() => {
    // Token'ı localStorage'dan al - farklı key'leri kontrol et
    const token =
      localStorage.getItem('penatoken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('penaToken')

    setPenaToken(token)
  }, [])

  async function getAllData() {
    try {
      // Token'ı tekrar kontrol et - farklı key'leri kontrol et
      const currentToken =
        localStorage.getItem('penatoken') ||
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('penaToken')

      if (!currentToken) {

        return
      }

      // Token değişmişse güncelle
      if (currentToken !== penaToken) {
        setPenaToken(currentToken)
      }

      setLoading(true)
      setError(null)

      let events = []
      let formattedEvents = []

      if (useComprehensiveAPI) {
        // Yeni kapsamlı API kullan
        const data = await calendarService.getAllCalendarItems(currentToken)

        events = data.calendar_items || []
        formattedEvents = calendarService.formatCalendarItemsForCalendar(events)
      } else {
        // Eski API kullan (geriye uyumluluk)

        events = await calendarService.getAllEvents(currentToken)

        formattedEvents = calendarService.formatEventsForCalendar(events)
      }

      setCalData(formattedEvents)

    } catch (error) {
      setError('Takvim verileri yüklenirken bir hata oluştu.')
      setCalData([])
      Notify.failure('Takvim verileri yüklenemedi!')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (penaToken) {
      getAllData()
    }
  }, [penaToken, useComprehensiveAPI])

  const calendarsColor = {
    0: '#cddc39', // Düşük - Açık yeşil
    1: '#2196f3', // Normal - Mavi
    2: '#ff9800', // Yüksek - Turuncu
    3: '#f44336' // Kritik - Kırmızı
  }

  const calendarOptions = {
    events: caldata,
    plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      end: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
    },
    locales: allLocales,
    locale: 'tr',
    dayMaxEvents: 4,
    navLinks: true,
    height: 'auto',
    aspectRatio: 1.8,
    eventDisplay: 'block',
    dayMaxEventRows: 3,
    moreLinkClick: 'popover',
    eventDidMount(info) {
      const priority = info.event.extendedProps?.priority
      const colors = {
        0: { bg: '#dcfce7', text: '#166534', border: '#22c55e' }, // Yeşil
        1: { bg: '#dbeafe', text: '#1d4ed8', border: '#3b82f6' }, // Mavi
        2: { bg: '#fef3c7', text: '#d97706', border: '#f59e0b' }, // Turuncu
        3: { bg: '#fee2e2', text: '#dc2626', border: '#ef4444' } // Kırmızı
      }
      const c = colors.hasOwnProperty(priority) ? colors[priority] : colors[1]
      const el = info.el
      // Tüm görünümler için CSS değişkenlerini ayarla
      el.style.setProperty('--fc-event-bg-color', c.bg)
      el.style.setProperty('--fc-event-border-color', c.border)
      el.style.setProperty('--fc-event-text-color', c.text)
      // DayGrid/TimeGrid ana elemanı da boyansın
      el.style.backgroundColor = c.bg
      el.style.color = c.text
      el.style.borderLeft = `3px solid ${c.border}`
    },

    // Modern görünüm için CSS sınıfları
    eventClassNames({ event: calendarEvent }) {
      const priority = calendarEvent._def.extendedProps.priority || 2
      const type = calendarEvent._def.extendedProps.type || 'calendar_event'
      return [`priority-${priority}`, `type-${type}`, 'modern-event']
    },

    // Renkler CSS sınıflarıyla (priority-*) yönetiliyor

    eventClick({ event: clickedEvent }) {
      const extendedProps = clickedEvent._def.extendedProps

      // Sadece calendar_event tipindeki öğeleri düzenlenebilir yap
      if (extendedProps.type === 'calendar_event') {
        setCurId(clickedEvent._def.publicId)
        setIsActive(true)
      } else {
        // Diğer öğe tipleri için bilgi göster
        const message = `
${clickedEvent.title}
Tip: ${calendarService.getItemTypeLabel(extendedProps.type)}
Açıklama: ${extendedProps.description}
Öncelik: ${extendedProps.priority}
${extendedProps.project_name ? `Proje: ${extendedProps.project_name}` : ''}
        `
        alert(message)
      }
    },

    dateClick(info) {
      setCurDate(info.date)
      setCurId('')
      setIsActive(true)
    },

    // Etkinlik oluşturma
    selectable: true,
    selectMirror: true,
    select(info) {
      setCurDate(info.start)
      setCurId('')
      setIsActive(true)
    },

    ref: calendarRef
  }

  return (
    <div className="calendar-page">
      <div className="calendar-container">
        <div className="calendar-header">
          <HeaderComponent title="Takvim" />
        </div>

        {error && (
          <div className="error-banner">
            <span>⚠️ {error}</span>
            <button onClick={getAllData} className="retry-btn">
              Tekrar Dene
            </button>
          </div>
        )}

        <div className="calendar-wrapper">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Takvim yükleniyor...</p>
            </div>
          ) : (
            <FullCalendar {...calendarOptions} />
          )}
        </div>

        {isActive && (
          <div className="event-panel-overlay" onClick={() => setIsActive(false)}>
            <div className="event-panel" onClick={(e) => e.stopPropagation()}>
              <EventTab
                getAllData={getAllData}
                setClose={() => {
                  setIsActive(false)
                  setCurId('')
                }}
                isActive={isActive}
                userid={
                  localStorage.getItem('penatoken') ||
                  localStorage.getItem('token') ||
                  localStorage.getItem('authToken') ||
                  localStorage.getItem('accessToken') ||
                  localStorage.getItem('penaToken')
                }
                curId={curId}
                curDate={curDate}
              />
            </div>
          </div>
        )}
      </div>

      <style jsx="true">{`
        .calendar-page {
          min-height: 100vh;
          background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 24px;
        }

        .calendar-container {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(2, 6, 23, 0.06);
          overflow: hidden;
          max-width: 1400px;
          margin: 0 auto;
          border: 1px solid #e2e8f0;
        }

        .calendar-header {
          background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
          border-bottom: 1px solid #e2e8f0;
          color: #1e293b;
          padding: 18px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .calendar-actions {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .api-toggle {
          display: flex;
          align-items: center;
        }

        .toggle-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .toggle-label input[type='checkbox'] {
          width: 18px;
          height: 18px;
          accent-color: #3b82f6;
        }

        .toggle-text {
          color: #64748b;
          font-size: 14px;
        }

        .refresh-btn {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #475569;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .refresh-btn:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }

        .refresh-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .event-count {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
        }

        .error-banner {
          background: #fee;
          border: 1px solid #fcc;
          color: #c33;
          padding: 15px 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .retry-btn {
          background: #c33;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }

        .calendar-wrapper {
          padding: 16px 20px 24px 20px;
          min-height: 600px;
          background: #ffffff;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 400px;
          color: #666;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .event-panel-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1000;
        }

        .event-panel {
          position: fixed;
          top: 0;
          right: 0;
          height: 100vh;
          width: 420px;
          max-width: 90vw;
          background: #ffffff;
          box-shadow: -8px 0 20px rgba(0, 0, 0, 0.15);
          overflow-y: auto;
          z-index: 1001;
          animation: slideInRight 0.25s ease-out;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0.5;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* FullCalendar özelleştirmeleri - Sade ve Modern */
        :global(.fc) {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          border: none !important;
        }

        :global(.fc-toolbar) {
          background: transparent !important;
          border: none !important;
          padding: 8px 0 16px 0 !important;
          margin-bottom: 0 !important;
        }

        :global(.fc-toolbar-title) {
          font-size: 1.4rem !important;
          font-weight: 600 !important;
          color: #1e293b !important;
          margin: 0 !important;
        }

        :global(.fc-button) {
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px !important;
          font-weight: 600 !important;
          color: #334155 !important;
          padding: 8px 14px !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 1px 2px rgba(2, 6, 23, 0.04) !important;
        }

        :global(.fc-button:hover) {
          background: #f8fafc !important;
          border-color: #cbd5e1 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 10px rgba(2, 6, 23, 0.06) !important;
        }

        :global(.fc-button-primary:not(:disabled):active) {
          background: #e2e8f0 !important;
        }

        :global(.fc-button-primary:disabled) {
          background: #f8fafc !important;
          color: #94a3b8 !important;
        }

        :global(.fc-button-group > .fc-button:focus) {
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1) !important;
        }

        /* Takvim başlığı */
        :global(.fc-col-header-cell) {
          background: #f8fafc !important;
          border: none !important;
          padding: 12px 8px !important;
          font-weight: 600 !important;
          color: #64748b !important;
          font-size: 0.875rem !important;
        }

        /* Takvim hücreleri */
        :global(.fc-daygrid-day) {
          border: 1px solid #f1f5f9 !important;
          background: #ffffff !important;
          transition: background-color 0.2s ease, box-shadow 0.2s ease !important;
        }

        :global(.fc-daygrid-day-number) {
          color: #475569 !important;
          font-weight: 500 !important;
          padding: 8px !important;
          font-size: 0.875rem !important;
        }

        :global(.fc-day-today) {
          background: #fef3c7 !important;
        }

        :global(.fc-day-today .fc-daygrid-day-number) {
          background: #f59e0b !important;
          color: white !important;
          border-radius: 50% !important;
          width: 28px !important;
          height: 28px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 600 !important;
        }

        /* Etkinlik stilleri - Sade ve Temiz */
        :global(.fc-event) {
          border: none !important;
          border-radius: 10px !important;
          font-weight: 500 !important;
          font-size: 12px !important;
          padding: 6px 10px !important;
          margin: 2px 0 !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06) !important;
          background: transparent !important; /* Renkler priority-* sınıflarından gelecek */
          color: inherit !important;
          transition: transform 0.15s ease, box-shadow 0.15s ease !important;
        }

        :global(.modern-event:hover) {
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
        }

        /* DayGrid görünümünde içerik hizalama ve başlık kırpma */
        :global(.fc-daygrid-event .fc-event-title) {
          display: -webkit-box !important;
          -webkit-line-clamp: 1 !important;
          -webkit-box-orient: vertical !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: normal !important;
        }

        :global(.fc-daygrid-event .fc-event-time) {
          font-weight: 700 !important;
          margin-right: 8px !important;
          padding: 2px 6px !important;
          border-radius: 6px !important;
          background: rgba(15, 23, 42, 0.06) !important;
        }

        /* TimeGrid görünümü: içerik okunabilirliği */
        :global(.fc-timegrid-event .fc-event-main) {
          padding: 4px 6px !important;
        }
        :global(.fc-timegrid-event .fc-event-time) {
          font-weight: 700 !important;
          opacity: 0.85 !important;
        }
        :global(.fc-timegrid-event .fc-event-title) {
          font-weight: 600 !important;
        }

        /* List görünümü: rozet ve satır düzeni */
        :global(.fc-list-event-title) {
          font-weight: 600 !important;
        }
        :global(.fc-list-event-time) {
          font-weight: 700 !important;
          padding: 2px 8px !important;
          border-radius: 6px !important;
          background: rgba(15, 23, 42, 0.06) !important;
        }

        :global(.fc-event-title) {
          font-weight: 500 !important;
          font-size: 0.75rem !important;
        }

        :global(.fc-event-time) {
          font-size: 0.7rem !important;
          opacity: 0.9 !important;
        }

        /* Öncelik renkleri - İstenen eşleşme: 0=Yeşil, 1=Mavi, 2=Turuncu, 3=Kırmızı */
        :global(.priority-0) {
          background: #dcfce7 !important; /* açık yeşil */
          color: #166534 !important; /* koyu yeşil metin */
          border-left: 3px solid #22c55e !important; /* yeşil */
        }

        :global(.priority-1) {
          background: #dbeafe !important; /* açık mavi */
          color: #1d4ed8 !important; /* mavi metin */
          border-left: 3px solid #3b82f6 !important; /* mavi */
        }

        :global(.priority-2) {
          background: #fef3c7 !important; /* açık turuncu/sarı */
          color: #d97706 !important; /* turuncu metin */
          border-left: 3px solid #f59e0b !important; /* turuncu */
        }

        :global(.priority-3) {
          background: #fee2e2 !important; /* açık kırmızı */
          color: #dc2626 !important; /* kırmızı metin */
          border-left: 3px solid #ef4444 !important; /* kırmızı */
        }

        /* Öğe tipi stilleri - Minimal */
        :global(.type-calendar_event) {
          border-left: 3px solid #3b82f6 !important;
        }

        :global(.type-user_task) {
          border-left: 3px solid #10b981 !important;
        }

        :global(.type-user_note) {
          border-left: 3px solid #f59e0b !important;
        }

        :global(.type-project_task) {
          border-left: 3px solid #8b5cf6 !important;
        }

        :global(.type-reminder) {
          border-left: 3px solid #ef4444 !important;
        }

        /* Hover efektleri */
        :global(.fc-event:hover) {
          transform: translateY(-1px) !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
        }

        :global(.fc-daygrid-event:hover) {
          cursor: pointer !important;
        }

        :global(.fc-daygrid-day:hover) {
          background: #f8fafc !important;
          box-shadow: inset 0 0 0 1px #e2e8f0 !important;
        }

        :global(.fc-daygrid-day.fc-day-today:hover) {
          background: #fef3c7 !important;
        }

        /* Daha fazla etkinlik linki */
        :global(.fc-more-link) {
          color: #64748b !important;
          font-size: 0.75rem !important;
          font-weight: 500 !important;
        }

        :global(.fc-more-link:hover) {
          color: #475569 !important;
        }

        /* Responsive tasarım */
        @media (max-width: 768px) {
          .calendar-page {
            padding: 10px;
          }

          .calendar-header {
            flex-direction: column;
            gap: 15px;
            text-align: center;
          }

          .calendar-wrapper {
            padding: 15px;
          }

          :global(.fc-toolbar) {
            flex-direction: column !important;
            gap: 10px !important;
          }

          :global(.fc-toolbar-chunk) {
            display: flex !important;
            justify-content: center !important;
          }

          .event-panel {
            width: 100vw;
            max-width: 100vw;
          }
        }
      `}</style>
    </div>
  )
}
